import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router';
import { Box, MenuItem, useTheme } from '@mui/material';

import { Tooltip, Typography } from '@zendesk/forethought-elements';
import { Scope } from '../../types';
import VariantPill from '../VariantPill';
import Menu from 'src/components/menu';
import { useEmitTrackingEventCallback } from 'src/hooks/hooks';
import {
  getWorkflowBuilderUrl,
  openTab,
} from 'src/pages/intent-conversation-analytics/helpers';
import {
  ABTestSample,
  ABTestVariantInfo,
  buildVariantByWorkflowId,
  InsightConversation,
  InsightRelatedArticle,
  InsightRelatedIntent,
  InsightSubtopic,
} from 'src/services/insights/types';
import { setGlobalWorkflowBuilderOptions } from 'src/slices/ui/uiSlice';
import { useAppDispatch } from 'src/store/hooks';
import { Routes } from 'src/utils/enums';

type OverflowDropdownVariant =
  | 'article'
  | 'workflow'
  | 'topic'
  | 'user_queries'
  | 'custom_events';

interface OverflowDropdownProps {
  abTestSamples?: ABTestSample[] | null;
  data:
    | InsightRelatedArticle[]
    | InsightRelatedIntent[]
    | InsightConversation['custom_events']
    | InsightSubtopic[]
    | string[];
  footer?: React.ReactNode;
  menuTitle: string;
  NavigateComponent?: React.ReactNode;
  onItemClick?: (id: string, optionalId?: string) => void;
  scope: Scope;
  tab: 'article' | 'workflow' | 'topic' | 'chat';
  titleSuffix?: string;
  variant?: OverflowDropdownVariant;
  width?: string;
}

const OverflowDropdown = ({
  abTestSamples,
  data,
  footer,
  menuTitle,
  NavigateComponent,
  onItemClick,
  scope,
  tab,
  titleSuffix,
  variant = 'article',
  width,
}: OverflowDropdownProps) => {
  const variantByWorkflowId = useMemo(() => {
    if (!abTestSamples || variant !== 'workflow') {
      return new Map<string, ABTestVariantInfo>();
    }
    return buildVariantByWorkflowId(abTestSamples);
  }, [abTestSamples, variant]);

  interface NormalizedItem {
    abTestVariantInfo?: ABTestVariantInfo;
    id: string;
    name: string;
    optionalId?: string;
  }

  const normalizedData = useMemo((): NormalizedItem[] => {
    if (variant === 'workflow') {
      return (data as InsightRelatedIntent[]).map(item => ({
        abTestVariantInfo: variantByWorkflowId.get(item.workflow_id),
        id: item.intent_id,
        name: item.intent_name,
        optionalId: item.workflow_id,
      }));
    }
    if (variant === 'topic') {
      return (data as InsightSubtopic[]).map(item => ({
        id: item.cluster_id,
        name: item.cluster_name,
      }));
    }
    if (variant === 'article') {
      return (data as InsightRelatedArticle[]).map(item => ({
        id: item.article_id,
        name: item.article_title,
      }));
    }
    if (variant === 'user_queries') {
      return (data as string[]).map(item => ({
        id: item,
        name: item,
      }));
    }
    if (variant === 'custom_events') {
      return (data as InsightConversation['custom_events']).map(item => ({
        id: item.event_configuration_id,
        name: item.event_configuration_name,
      }));
    }
    return [];
  }, [data, variant, variantByWorkflowId]);

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        gap: 1,
        width: width ? width : '200px}',
      }}
    >
      {normalizedData.length > 0 && (
        <TitleWithMenu
          abTestVariantInfo={normalizedData[0].abTestVariantInfo}
          firstItemId={normalizedData[0].id ?? ''}
          Menu={DropdownMenu}
          MenuProps={{
            firstItemTitle: normalizedData[0].name ?? '',
            menuItems: [
              ...normalizedData.map(item => (
                <ComponentMenuItem
                  abTestVariantInfo={item.abTestVariantInfo}
                  id={item.id}
                  key={item.id}
                  onItemClick={onItemClick}
                  optionalId={item.optionalId}
                  scope={scope}
                  tab={tab}
                  title={item.name}
                  variant={variant}
                />
              )),
              footer,
            ].filter(Boolean),
            NavigateComponent,
            scope: scope,
            tab: tab,
            title: menuTitle,
            variant: variant,
          }}
          onItemClick={onItemClick}
          optionalId={normalizedData[0]?.optionalId}
          tab={tab}
          titleSuffix={titleSuffix}
          variant={variant}
        />
      )}
    </Box>
  );
};

interface TitleWithMenuProps {
  abTestVariantInfo?: ABTestVariantInfo;
  firstItemId: string;
  Menu: typeof DropdownMenu;
  MenuProps: {
    firstItemTitle: string;
    menuItems?: React.ReactNode[];
    NavigateComponent?: React.ReactNode;
    scope: Scope;
    tab: 'article' | 'workflow' | 'topic' | 'chat';
    title: string;
    variant?: OverflowDropdownVariant;
  };
  onItemClick?: (id: string, optionalId?: string) => void;
  optionalId?: string;
  tab: 'article' | 'workflow' | 'topic' | 'chat';
  titleSuffix?: string;
  variant?: OverflowDropdownVariant;
}

const TitleWithMenu = ({
  abTestVariantInfo,
  firstItemId,
  Menu,
  MenuProps,
  onItemClick,
  optionalId = '',
  tab,
  titleSuffix,
  variant,
}: React.PropsWithChildren<TitleWithMenuProps>) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { palette } = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchorEl);

  // Hooks
  const emitTrackingEventCallback = useEmitTrackingEventCallback();

  const arrayLengthByVariant = MenuProps.menuItems?.length ?? 0;
  const shouldShowMenu = arrayLengthByVariant > 1;

  const handleClickMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    emitTrackingEventCallback('insight-overflow-menu-open', {
      scope: MenuProps.scope,
      tab,
      type: variant,
    });
  };

  const [searchParams, setSearchParams] = useSearchParams();

  const handleClickLink = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (onItemClick) {
      onItemClick(firstItemId, optionalId);
      return;
    }
    if (variant === 'custom_events') {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('custom-events-view', 'edit');
      newSearchParams.set('custom-events-event-configuration-id', firstItemId);
      setSearchParams(newSearchParams);
      dispatch(
        setGlobalWorkflowBuilderOptions({
          isCustomEventsDrawerOpen: true,
        }),
      );

      emitTrackingEventCallback('insight-overflow-surfaced-data-clicked', {
        id: firstItemId,
        scope: MenuProps.scope,
        tab: tab,
        type: variant,
        value: String(newSearchParams),
      });

      return;
    }

    let pathName = '';
    if (variant === 'workflow') {
      pathName = Routes.SOLVE_INSIGHTS_WORKFLOW_DETAIL.replace(
        ':workflowId',
        optionalId,
      );
    } else if (variant === 'topic') {
      pathName = Routes.SOLVE_INSIGHTS_TOPIC_DETAIL.replace(
        ':topicId',
        firstItemId,
      );
    } else {
      pathName = Routes.SOLVE_INSIGHTS_ARTICLE_DETAIL.replace(
        ':articleId',
        firstItemId,
      );
    }
    emitTrackingEventCallback('insight-overflow-surfaced-data-clicked', {
      id: firstItemId,
      scope: MenuProps.scope,
      tab: tab,
      type: variant,
      value: pathName,
    });

    navigate({
      pathname: pathName,
    });
  };

  const handleSuffixLabel = () => {
    if (titleSuffix) {
      return ` ${titleSuffix}`;
    }

    if (shouldShowMenu) {
      return ` +${arrayLengthByVariant - 1}`;
    }

    return '';
  };

  const suffix = handleSuffixLabel();

  return (
    <>
      <Box
        component='div'
        onClick={shouldShowMenu ? handleClickMenu : handleClickLink}
        sx={{
          '.MuiTypography-root': {
            fontStyle: variant === 'user_queries' ? 'italic' : undefined,
            textDecoration:
              variant === 'user_queries' ? undefined : 'underline',
          },

          ':hover': {
            bgcolor: palette.colors.slate[200],
            cursor: 'pointer',
          },

          alignItems: 'center',
          bgcolor:
            variant === 'workflow' || variant === 'custom_events'
              ? palette.colors.slate[100]
              : palette.colors.white,
          borderRadius: '4px',
          display: 'flex',
          lineHeight: '14px',

          padding: '4px 8px',

          width: 'fit-content',
        }}
      >
        <Box
          sx={{
            maxWidth: variant === 'user_queries' ? '150px' : '190px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          <Typography
            color={
              variant === 'workflow' || variant === 'custom_events'
                ? palette.colors.grey[700]
                : palette.colors.blue[600]
            }
            noWrap
            variant={variant === 'user_queries' ? 'font14' : 'font12Medium'}
          >
            {variant === 'user_queries' && '"'}
            {MenuProps.firstItemTitle}
            {variant === 'user_queries' && '"'}
          </Typography>
        </Box>
        <Typography
          color={
            variant === 'workflow' || variant === 'custom_events'
              ? palette.colors.grey[700]
              : palette.colors.blue[600]
          }
          noWrap
          variant={variant === 'user_queries' ? 'font14' : 'font12Medium'}
        >
          {suffix}
        </Typography>
        {abTestVariantInfo && !shouldShowMenu && (
          <Box
            onClick={e => {
              e.stopPropagation();
              if (!optionalId) {
                return;
              }
              openTab(
                getWorkflowBuilderUrl({
                  intentWorkflowId: optionalId,
                  version: abTestVariantInfo.intent_workflow_version,
                  versionType: 'published',
                }),
              );
            }}
            sx={{ cursor: 'pointer', flexShrink: 0, ml: '4px' }}
          >
            <VariantPill variant={abTestVariantInfo} />
          </Box>
        )}
      </Box>
      {isOpen && (
        <Menu
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          {...MenuProps}
        />
      )}
    </>
  );
};

interface DropdownMenuProps {
  anchorEl: null | HTMLElement;
  menuItems?: React.ReactNode[];
  NavigateComponent?: React.ReactNode;
  onClickMenuItem?: (value: string) => void;
  onClose: () => void;
  scope: Scope;
  tab: 'article' | 'workflow' | 'topic' | 'chat';
  title: string;
  variant?: OverflowDropdownVariant;
}

const DropdownMenu = ({
  anchorEl,
  menuItems = [],
  NavigateComponent,
  onClickMenuItem,
  onClose,
  scope,
  tab,
  title,
  variant,
}: DropdownMenuProps) => {
  const { palette } = useTheme();
  // Hooks
  const emitTrackingEventCallback = useEmitTrackingEventCallback();

  return (
    <Menu
      anchorEl={anchorEl}
      MenuListProps={{
        role: 'listbox',
        sx: {
          py: 1.5,
        },
      }}
      onClick={e => e.stopPropagation()}
      onClose={e => {
        //Casting is needed as MUI has {} as a type for events
        const event = e as MouseEvent;
        event.stopPropagation();
        onClose();
        emitTrackingEventCallback('insight-overflow-menu-close', {
          scope,
          tab,
          type: variant,
        });
      }}
      open={Boolean(anchorEl)}
      title={title}
      width='350px'
    >
      {menuItems.map((node, index) => (
        <MenuItem
          key={index}
          onClick={() => onClickMenuItem?.('x')}
          sx={{
            '&:hover': {
              backgroundColor:
                variant === 'user_queries'
                  ? 'transparent'
                  : palette.colors.primary[100],
            },
            alignItems: 'center',
            backgroundColor: 'transparent',
            cursor: variant === 'user_queries' ? 'text' : 'default',
            display: 'flex',
            padding: '4px 16px',
            userSelect: variant === 'user_queries' ? 'initial' : undefined,
          }}
        >
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flex: 1,
              overflow: 'hidden',
              textWrap: 'wrap',
            }}
          >
            {node}
          </Box>
        </MenuItem>
      ))}
      {NavigateComponent && NavigateComponent}
    </Menu>
  );
};

interface ComponentMenuItemProps {
  abTestVariantInfo?: ABTestVariantInfo;
  id: string;
  onItemClick?: (id: string, optionalId?: string) => void;
  optionalId?: string;
  scope: Scope;
  tab: 'article' | 'workflow' | 'topic' | 'chat';
  title: string;
  variant?: OverflowDropdownVariant;
}

const ComponentMenuItem = ({
  abTestVariantInfo,
  id,
  onItemClick,
  optionalId = '',
  scope,
  tab,
  title,
  variant,
}: ComponentMenuItemProps) => {
  const { t } = useTranslation('solve-insights');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { palette } = useTheme();

  // Hooks
  const emitTrackingEventCallback = useEmitTrackingEventCallback();

  const [searchParams, setSearchParams] = useSearchParams();

  const getViewVariantText = (variant?: OverflowDropdownVariant): string => {
    switch (variant) {
      case 'workflow':
        return t('common.overflowDropdown.variant.workflow');
      case 'custom_events':
        return t('common.overflowDropdown.variant.customEvent');
      case 'topic':
        return t('common.overflowDropdown.variant.topic');
      default:
        return t('common.overflowDropdown.variant.article');
    }
  };

  const handleClickLink = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (onItemClick) {
      onItemClick(id, optionalId);
      return;
    }
    if (variant === 'custom_events') {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('custom-events-view', 'edit');
      newSearchParams.set('custom-events-event-configuration-id', id);
      setSearchParams(newSearchParams);
      dispatch(
        setGlobalWorkflowBuilderOptions({
          isCustomEventsDrawerOpen: true,
        }),
      );

      emitTrackingEventCallback('insight-overflow-surfaced-data-clicked', {
        id,
        scope,
        tab: tab,
        type: variant,
        value: String(newSearchParams),
      });

      // DIRTY HACK: close the menu by clicking on the backdrop.
      // Other ways (like `onClickMenuItem={() => setAnchorEl(null)}`) didn't work.
      document
        .querySelector<HTMLDivElement>('.MuiBackdrop-root[style*="opacity: 1"]')
        ?.click();

      return;
    }

    let pathName = '';
    if (variant === 'workflow') {
      pathName = Routes.SOLVE_INSIGHTS_WORKFLOW_DETAIL.replace(
        ':workflowId',
        optionalId,
      );
    } else if (variant === 'topic') {
      pathName = Routes.SOLVE_INSIGHTS_TOPIC_DETAIL.replace(':topicId', id);
    } else {
      pathName = Routes.SOLVE_INSIGHTS_ARTICLE_DETAIL.replace(':articleId', id);
    }

    emitTrackingEventCallback('insight-overflow-surfaced-data-clicked', {
      id,
      scope,
      tab: tab,
      type: variant,
      value: pathName,
    });

    navigate({
      pathname: pathName,
    });
  };

  return variant === 'user_queries' ? (
    <Box
      sx={{
        fontStyle: 'italic',
        marginBottom: '12px',
      }}
    >
      <Typography color={palette.colors.blue[600]} variant='font14'>
        {t('common.overflowDropdown.quotedTitle', { title })}
      </Typography>
    </Box>
  ) : (
    <Box
      onClick={handleClickLink}
      sx={{
        '& > span:last-of-type': { display: 'none' },
        '&:hover > span:last-of-type': { display: 'inline' },
        alignItems: 'center',
        cursor: 'pointer',
        display: 'flex',
        flex: 1,
        gap: '6px',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          flex: 1,
          overflow: 'hidden',
        }}
      >
        <Tooltip tooltipContent={title}>
          <Typography color={palette.colors.black} noWrap variant='font14'>
            {title}
          </Typography>
        </Tooltip>
      </Box>
      {abTestVariantInfo && (
        <Box
          onClick={e => {
            e.stopPropagation();
            if (!optionalId) {
              return;
            }
            openTab(
              getWorkflowBuilderUrl({
                intentWorkflowId: optionalId,
                version: abTestVariantInfo.intent_workflow_version,
                versionType: 'published',
              }),
            );
          }}
          sx={{ cursor: 'pointer', flexShrink: 0 }}
        >
          <VariantPill variant={abTestVariantInfo} />
        </Box>
      )}
      <Typography color={palette.primary.main} variant='font11'>
        {t('common.overflowDropdown.viewVariant', {
          variant: getViewVariantText(variant),
        })}
      </Typography>
    </Box>
  );
};

export default OverflowDropdown;
